import './public_path';
import h from 'hyperscript';
import 'rendering/styling/embed.scss';
import { injectSEO } from 'utilities/seo';
import { safeJSON } from 'utilities/parsers';
import { documentReady } from 'utilities/dom';
import { EMBED_TYPES } from 'constants';
import { getAuthorization, pageSource } from 'utilities/restriction';
import { PreviewMessageHTML } from 'utilities/preview';
import { EXTERNAL_EVENT_MAP as apiEvents } from 'api/player/event_maps';
import { customElement, customEvent } from 'utilities/polyfills'
import { LightboxCustomElement, InlineCustomElement } from 'src/embed/custom-element';
import * as is from '../utilities/is';

export const HAPYAK_EMBED_TAG_NAME = 'hapyak-embed';
const ESCAPE_KEY = 27;

// we might delay definition of the class so we need to proxy the definition behind a Promise for later export
let classDefinition; 

if ('customElements' in window) {
    classDefinition = Promise.resolve(
        defineElement()
    );
} else {
    classDefinition = (
        Promise.all([
            customElement(),
            customEvent()
        ])
        .then(() => customElements.forcePolyfill = true)
        .then(defineElement)
    );
}

export const DEFAULT_ATTRIBUTES = {
    PRESENTATION: 'lightbox',
    IFRAME: false
};

function defineElement() {
    // expose class members privately using Symbol
    const getAttributes = Symbol(),
        embedManager = Symbol(),
        onEmbedClick = Symbol(),
        generateEmbedManager = Symbol();

    class HapyakEmbed extends HTMLElement {
        constructor() {
            super();

            // use before embed manager or player is available
            this.playbackStateCache = {
                duration: NaN,
                paused: true,
                volume: 1,
                currentTime: 0,
                language: 'en'
            };
        }

        // Custom Element Overrides

        connectedCallback() {
            this[getAttributes]().then(attributes => {
                this[embedManager] = this[generateEmbedManager](attributes);
                this.appendChild(this[embedManager].embed);
            });
        }

        disconnectedCallback() {
            if (this[embedManager] && this[embedManager].embed && this.removeChild) {
                try {
                    this.removeChild(this[embedManager].embed);
                } catch (e) {
                    // no actions required
                }
            }
        }

        attributeChangedCallback(name, oldValue, newValue) {
            switch(name) {
                case 'src':
                    if (this[embedManager]) {
                        this[embedManager].src = newValue;
                    }

                    break;
            }
        }

        // Private Class Members

        [generateEmbedManager](attributes) {
            const { presentation } = attributes;

            switch(presentation) {
                case 'lightbox':
                    return new LightboxCustomElement(this, attributes);

                case 'inline':
                    return new InlineCustomElement(this, attributes);
            }
        }

        [getAttributes]() {
            const eventAttributes = Object.keys(apiEvents).reduce((attrs, eventName) => {
                eventName = `on${eventName}`;
                attrs[eventName] = this[eventName] || this.getAttribute(eventName);

                if (typeof attrs[eventName] === 'string') {
                    // non standard attribute callbacks aren't converted to functions, so we need to do that manually
                    attrs[eventName] = new Function('event', attrs[eventName]);
                }

                return attrs;
            }, {});

            // child elements may be used for embed configuration in addition to attributes
            // we need to wait for them to be parsed before providing other attributes
            return (
                documentReady()
                    .then(() => {
                        const iframe = 
                            this.iframe === 'true' ||
                            this.iframe === true ||
                            this.getAttribute('iframe') === 'true' ||
                            this.getAttribute('iframe') === true ||
                            DEFAULT_ATTRIBUTES.IFRAME;

                        const attributes = {
                            ...eventAttributes,
                            iframe,
                            title: getTitle(this),
                            description: getDescription(this),
                            uid: this.uid || this.getAttribute('uid'),
                            src: this.src || this.getAttribute('src'),
                            lang: this.lang || this.getAttribute('lang') || this.playbackStateCache.language,
                            captions: (this.captions === false || this.getAttribute('captions') === 'false') ? false : true,
                            loop: this.loop === 'true' || emptyAttributeDefined('loop', this),
                            width: this.width || this.getAttribute('width'),
                            height: this.height || this.getAttribute('height'),
                            start: this.start || this.getAttribute('start') || this.playbackStateCache.currentTime,
                            refId: this.refId || this.getAttribute('ref-id'),
                            autoplay: this.autoplay || emptyAttributeDefined('autoplay', this),
                            thumbnail: this.thumbnail || this.getAttribute('thumbnail'),
                            variables: getVariables(this),
                            playButton: this['play-button'] === 'true' || emptyAttributeDefined('play-button', this),
                            textLocation: this['text-location'] || this.getAttribute('text-location') || 'overlay',
                            presentation: this.presentation || this.getAttribute('presentation') || DEFAULT_ATTRIBUTES.PRESENTATION,                            
                            level: this.level || this.getAttribute('level'),
                            context: safeJSON(this.context || this.getAttribute('context'))
                        };

                        if (attributes.autoplay === 'false') {
                            attributes.autoplay = false;
                        } else if (attributes.autoplay === 'true') {
                            attributes.autoplay = true;
                        }

                        return attributes;
                    })
            )
        }

        // Public Class Members

        play() {
            this[embedManager] && this[embedManager].player && this[embedManager].player.play();
        }

        pause() {
            this[embedManager] && this[embedManager].player && this[embedManager].player.pause();
        }

        getVariable(name) {
            return this[embedManager] && this[embedManager].player && this[embedManager].player.getVariable(name);
        }

        setVariable(name, value) {
            this[embedManager] && this[embedManager].player && this[embedManager].player.setVariable(name, value);
        }

        get eventBus() {
            return this[embedManager]?.player?.eventBus
        }

        get language() {
            return (this[embedManager] && this[embedManager].player && this[embedManager].player.language) || this.playbackStateCache.language;
        }

        set language(languageCode) {
            if (this[embedManager] && this[embedManager].player && languageCode) {
                this[embedManager].player.language = languageCode;
            } else if (languageCode) {
                this.playbackStateCache.language = languageCode;
            }
        }

        get level() {
            return this[embedManager]?.player?.level || this.playbackStateCache.level;
        }

        set level(level) {
            if (this[embedManager]?.player && level) {
                this[embedManager].player.level = level;
            } else {
                this.playbackStateCache.level;
            }
        }

        get levels() {
            return this[embedManager]?.player?.data?.project?.levels;
        }

        get currentTime() {
            return (this[embedManager] && this[embedManager].player && this[embedManager].player.currentTime) || this.playbackStateCache.currentTime;
        }

        set currentTime(time) {
            if (this[embedManager] && this[embedManager].player) {
                this[embedManager].player.currentTime = time;
            } else {
                this.playbackStateCache.currentTime = time;
            }
        }

        get paused() {
            return (this[embedManager] && this[embedManager].player && this[embedManager].player.paused) || this.playbackStateCache.paused;
        }

        get duration() {
            return (this[embedManager] && this[embedManager].player && this[embedManager].player.duration) || this.playbackStateCache.duration;
        }

        get volume() {
            const val = this?.[embedManager]?.player?.volume;
            return is.number(val) ? val : this.playbackStateCache.volume;
        }

        set volume(vol) {
            if (this[embedManager] && this[embedManager].player) {
                this[embedManager].player.volume = vol;
            } else {
                this.playbackStateCache.volume = vol;
            }
        }

        get context() {
            return this[embedManager]?.player?.data?.context;
        }

        set context(context) {
            console.warn('context cannot be changed after embed has been created!');
        }

        static get observedAttributes() {
            return [
                'src',
                'uid',
                'autoplay',
                'presentation',
                'width',
                'height',
                'loop',
                'lang',
                'start',
                'ref-id',
                'thumbnail',
                'play-button',
                'text-location',
                'captions',
                'level',
                'context',
                ...Object.keys(apiEvents).map(name => `on${name}`)
            ];
        }
    }

    if (!customElements.get(HAPYAK_EMBED_TAG_NAME)) {
        // prevent HapyakEmbed from being defined more than once
        customElements.define(HAPYAK_EMBED_TAG_NAME, HapyakEmbed);
    }

    return HapyakEmbed;

    // some attributes can be set without a value, so we need to capture all permutations of those attributes
    function emptyAttributeDefined(name, attrProvider) {
        const value = attrProvider && attrProvider.getAttribute && attrProvider.getAttribute(name);
        return value === '' || value === 'true' || value === name;
    }

    function getVariables(parent) {
        return (
            Array
                .from(parent.querySelectorAll('variable'))
                .map(el => ({ [el.name || el.getAttribute('name')]: el.innerText }))
                .reduce((variables, obj) => ({ ...variables, ...obj }), {})
        )
    }

    function getTitle(parent) {
        const title = parent.querySelector('title');

        return (title && title.innerText) || '';
    }

    function getDescription(parent) {
        const description = parent.querySelector('description');

        return (description && description.innerText) || '';
    }
}

export const getClass = () => classDefinition;